<template>
    <v-app>
        <app-layout v-if="authorized"/>
        <!-- Service Unavailable Screen (502 errors) -->
        <div v-else-if="serviceUnavailable" class="d-flex justify-center align-center" style="height: 100vh; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);">
            <v-card class="pa-8" max-width="800" elevation="12">
                <div class="text-center mb-4">
                    <v-icon size="80" color="warning">mdi-cloud-off-outline</v-icon>
                </div>
                <v-card-title class="text-h5 text-center">Service Temporarily Unavailable</v-card-title>
                <v-card-text class="text-center text-body-1 mt-2">
                    The service is currently undergoing maintenance or is temporarily unavailable.
                    Please try again in a few minutes.
                </v-card-text>
                <v-card-actions class="justify-center mt-4">
                    <v-btn color="primary" size="large" @click="retryAuth" :loading="retrying">
                        <v-icon left>mdi-refresh</v-icon>
                        Try Again
                    </v-btn>
                </v-card-actions>
            </v-card>
        </div>
        <!-- Session Expired Screen -->
        <div v-else-if="sessionExpired" class="d-flex justify-center align-center" style="height: 100vh; background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);">
            <v-card class="pa-8" max-width="500" elevation="12">
                <div class="text-center mb-4">
                    <v-icon size="80" color="info">mdi-timer-sand-empty</v-icon>
                </div>
                <v-card-title class="text-h4 text-center">Session Expired</v-card-title>
                <v-card-text class="text-center text-body-1 mt-2">
                    Your session has expired due to inactivity.
                    Please log in again to continue.
                </v-card-text>
                <v-card-actions class="justify-center mt-4">
                    <v-btn color="primary" size="large" @click="forceReLogin">
                        <v-icon left>mdi-login</v-icon>
                        Log In Again
                    </v-btn>
                </v-card-actions>
            </v-card>
        </div>
        <!-- Authentication Error Screen -->
        <div v-else-if="authError" class="d-flex justify-center align-center" style="height: 100vh;">
            <v-card class="pa-6" max-width="400">
                <v-card-title class="text-h5 text-error">Authentication Error</v-card-title>
                <v-card-text>{{ authError }}</v-card-text>
                <v-card-actions>
                    <v-btn color="primary" @click="retryAuth">Retry</v-btn>
                    <v-btn color="secondary" href="/saml/login">Login</v-btn>
                </v-card-actions>
            </v-card>
        </div>
        <!-- Loading Screen -->
        <div v-else class="d-flex justify-center align-center" style="height: 100vh;">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
        </div>
        <k-view-messages/>
    </v-app>
</template>

<script lang="ts">
import {defineComponent, provide, ref, onMounted, onUnmounted} from 'vue'
import {useRoute} from 'vue-router'
import axios from 'axios'
import KViewMessages from "@/components/dds/KViewMessages/KViewMessages.vue";
import AppLayout from "@/layout/AppLayout.vue";
import i18n from "@/plugins/i18n";
import {useAuthorizationStore} from "@/stores/AuthorizationStore/AuthorizationStore";
import {useApiCredentials} from "@/composables/useApiCredentials";
import {useAgentStore} from "@/stores/AgentStore/AgentStore";

export default defineComponent({
    components: {
        AppLayout, KViewMessages
    },
    setup() {
        const authorized = ref(false)
        const authError = ref<string | null>(null)
        const serviceUnavailable = ref(false)
        const sessionExpired = ref(false)
        const retrying = ref(false)

        // Inactivity timeout configuration (15 minutes in milliseconds)
        const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000
        let inactivityTimer: ReturnType<typeof setTimeout> | null = null

        provide('currentLocale', i18n.global.locale);
        const route = useRoute()

        const {rootGuid, guid, apikey, setRootCredentials} = useApiCredentials();
        const authStore = useAuthorizationStore();
        const agentStore = useAgentStore();

        // Activity tracking functions
        const resetInactivityTimer = () => {
            if (inactivityTimer) {
                clearTimeout(inactivityTimer)
            }
            if (authorized.value) {
                inactivityTimer = setTimeout(() => {
                    handleSessionTimeout()
                }, INACTIVITY_TIMEOUT_MS)
            }
        }

        const handleSessionTimeout = () => {
            authorized.value = false
            sessionExpired.value = true
            // Clear any stored tokens/credentials
            authStore.initialized = false
            authStore.items.length = 0
        }

        const startActivityTracking = () => {
            // Events that indicate user activity
            const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click']

            activityEvents.forEach(event => {
                document.addEventListener(event, resetInactivityTimer, { passive: true })
            })

            // Start the initial timer
            resetInactivityTimer()
        }

        const stopActivityTracking = () => {
            const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click']

            activityEvents.forEach(event => {
                document.removeEventListener(event, resetInactivityTimer)
            })

            if (inactivityTimer) {
                clearTimeout(inactivityTimer)
                inactivityTimer = null
            }
        }

        const forceReLogin = () => {
            sessionExpired.value = false
            // Redirect to login page
            globalThis.location.href = '/saml/login'
        }

        const initializeApp = async (credentials: {id?: number, guid: string, apikey: string, name?: string}[], isSamlAuth: boolean = false) => {
            if (credentials.length > 0) {
                // Store all credentials in authStore.items for the ApplicationPicker
                const mappedCredentials = credentials.map((c, index) => ({
                    id: c.id || index + 1,
                    guid: c.guid,
                    apikey: c.apikey,
                    name: c.name || `Application ${index + 1}`
                }))

                // Set items in authStore (cast needed due to store typing)
                authStore.items.length = 0
                mappedCredentials.forEach(c => authStore.items.push(c as any))
                authStore.initialized = true

                // Restore previously selected app from session, or use first app
                const storedAppId = sessionStorage.getItem('kmls_active_app_id')
                let activeApp = mappedCredentials[0]
                if (storedAppId) {
                    const stored = mappedCredentials.find(c => c.id === Number(storedAppId))
                    if (stored) activeApp = stored
                }
                authStore.activeApp = activeApp as any

                guid.value = activeApp.guid
                apikey.value = activeApp.apikey
                sessionStorage.setItem('kmls_active_app_id', String(activeApp.id))

                // For SAML auth, set user credentials as root credentials
                // This allows stores using rootAuth/rootGuid to work with SAML auth
                if (isSamlAuth) {
                    setRootCredentials(activeApp.guid, activeApp.apikey)
                }

                // Acquire root token first (needed by stores)
                await authStore.aquire_token(rootGuid.value, true);

                // Then acquire user token
                await authStore.aquire_token(guid.value, false);
                await authStore.currentApp()
                await agentStore.get_available_models();
                authorized.value = true
                // Start activity tracking after successful authorization
                startActivityTracking()
            } else {
                authError.value = 'No application credentials found for your account.'
            }
        }

        const trySamlAuth = async (): Promise<boolean> => {
            try {
                // Check if user is authenticated via /saml/userinfo (backend endpoint)
                const userInfoResponse = await fetch('/saml/userinfo')

                // Handle 502 Bad Gateway - Backend not available
                if (userInfoResponse.status === 502 || userInfoResponse.status === 503 || userInfoResponse.status === 504) {
                    serviceUnavailable.value = true
                    return true
                }

                const userInfo = await userInfoResponse.json()

                if (userInfo.authenticated) {
                    // User is authenticated via SAML, get credentials from backend
                    const credentialsResponse = await fetch('/saml/credentials').then(r => r.json())
                    await initializeApp(credentialsResponse, true)
                    return true
                } else if (userInfo.saml_available || userInfo.saml_configured) {
                    // SAML is configured but user not logged in - redirect to SAML login
                    globalThis.location.href = userInfo.login_url || '/saml/login'
                    return true
                } else {
                    // SAML not configured - show error
                    authError.value = 'SAML authentication is not configured. Please contact your administrator.'
                    return true
                }
            } catch (error: any) {
                // Handle network errors or fetch failures (likely backend down)
                if (error instanceof TypeError && error.message.includes('fetch')) {
                    serviceUnavailable.value = true
                    return true
                }
                // If 401, user needs to login
                if (error.response?.status === 401) {
                    globalThis.location.href = '/saml/login'
                    return true
                }
                // Handle 502/503/504 errors from axios
                if (error.response?.status === 502 || error.response?.status === 503 || error.response?.status === 504) {
                    serviceUnavailable.value = true
                    return true
                }
                authError.value = 'Authentication failed. Please try again.'
            }
            return false
        }

        const retryAuth = async () => {
            retrying.value = true
            authError.value = null
            serviceUnavailable.value = false
            authorized.value = false
            await authenticate()
            retrying.value = false
        }

        const authenticate = async () => {
            await trySamlAuth()
        }

        onMounted(() => {
            authenticate()
        })

        onUnmounted(() => {
            stopActivityTracking()
        })

        return {
            authorized,
            authError,
            serviceUnavailable,
            sessionExpired,
            retrying,
            retryAuth,
            forceReLogin,
            route
        }
    },
})
</script>

<style lang="scss" scoped>
div:deep(.v-application--wrap) {
    min-height: calc(100vh - 65px) !important;
}
</style>
